/**
 * Supabank Logo Component
 *
 * CANONICAL logo from SupaBankLogoShowcase.tsx (LogoBrand)
 * This is the OFFICIAL logo - matches Brand Guidelines exactly.
 *
 * Variants:
 * - full: Glyph + Wordmark "supa|bank" / ".xyz"
 * - icon: Zigzag glyph only
 * - wordmark: Text only "supabank.xyz"
 *
 * Features:
 * - Hover animation: Crypto Mint pulse on hover
 * - LED Heartbeat: Always visible (#FF2A00), blinks every 5s
 *
 * SSOT: /design/canon → SupaBankLogoShowcase.tsx (LogoBrand)
 */

import React from 'react';
import { cn } from '@/lib/utils';

export type LogoVariant = 'full' | 'icon' | 'wordmark';
export type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SupabankLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  /** For dark backgrounds */
  inverted?: boolean;
  /** Enable Living Brand (hover pulse + LED) - default true */
  living?: boolean;
}

// Brand colors (SSOT: SupaBankLogoShowcase.tsx)
export const BRAND_COLORS = {
  accent: '#3B82F6',      // Supabank Blue
  obsidian: '#0D1F1A',    // Dark ceramic
  light: '#F2F4F6',       // Light background
  cryptoMint: '#10B77F',  // Crypto Mint (pulse)
  ledRed: '#FF2A00',      // LED Heartbeat
} as const;

// Official zigzag glyph path (from SupaBankLogoShowcase)
const GLYPH_PATH = 'M 196 48 L 72 112 L 184 144 L 60 208';

// Size config - matches LogoBrand from SupaBankLogoShowcase
const sizeConfig: Record<LogoSize, {
  glyph: number;
  stroke: number;
  text: string;
  domain: string;
  gap: string;
  ledR: number;
}> = {
  xs: { glyph: 20, stroke: 0.8, text: 'text-xs', domain: 'text-[7px]', gap: 'gap-1', ledR: 1.5 },
  sm: { glyph: 28, stroke: 1.0, text: 'text-sm', domain: 'text-[9px]', gap: 'gap-1.5', ledR: 2 },
  md: { glyph: 36, stroke: 1.3, text: 'text-base', domain: 'text-[10px]', gap: 'gap-2', ledR: 2.5 },
  lg: { glyph: 44, stroke: 1.5, text: 'text-lg', domain: 'text-xs', gap: 'gap-2', ledR: 3 },
  xl: { glyph: 52, stroke: 1.8, text: 'text-xl', domain: 'text-sm', gap: 'gap-2.5', ledR: 3.5 },
};

/**
 * Zigzag Glyph Icon - CANONICAL from LogoBrand
 */
function ZigzagGlyph({
  size,
  config,
  inverted = false,
  showLed = true,
  hoverAnimation = true,
  uniqueId,
}: {
  size: LogoSize;
  config: typeof sizeConfig[LogoSize];
  inverted?: boolean;
  showLed?: boolean;
  hoverAnimation?: boolean;
  uniqueId: string;
}) {
  const { glyph, stroke, ledR } = config;
  const isDark = inverted;
  const accent = BRAND_COLORS.accent;

  return (
    <svg
      viewBox="0 0 256 256"
      width={glyph}
      height={glyph}
      className="overflow-visible flex-shrink-0"
      aria-hidden="true"
    >
      <defs>
        <path id={`glyph-${uniqueId}`} d={GLYPH_PATH} />
        {/* Glow filter for pulse */}
        <filter id={`glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="12" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* LED glow filter */}
        <filter id={`led-${uniqueId}`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Layer 1: Outer Glow */}
      <use
        href={`#glyph-${uniqueId}`}
        stroke={isDark ? accent : BRAND_COLORS.obsidian}
        strokeWidth={28 * stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: 'blur(12px)', opacity: isDark ? 0.5 : 0.15 }}
      />

      {/* Layer 2: Core body */}
      <use
        href={`#glyph-${uniqueId}`}
        stroke={isDark ? '#fff' : BRAND_COLORS.obsidian}
        strokeWidth={20 * stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: isDark ? 0.35 : 0.8 }}
      />

      {/* Layer 3: Static accent line */}
      <use
        href={`#glyph-${uniqueId}`}
        stroke={isDark ? accent : BRAND_COLORS.obsidian}
        strokeWidth={14 * stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={hoverAnimation ? `brand-static-${uniqueId}` : ''}
        style={{ opacity: hoverAnimation ? undefined : 1 }}
      />

      {/* Layer 4: Animated Pulse (on hover) */}
      {hoverAnimation && (
        <use
          href={`#glyph-${uniqueId}`}
          stroke={BRAND_COLORS.cryptoMint}
          strokeWidth={14 * stroke}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="100 600"
          filter={`url(#glow-${uniqueId})`}
          className={`brand-pulse-${uniqueId}`}
        />
      )}

      {/* Layer 5: LED indicator (always blinks) */}
      {showLed && size !== 'xs' && (
        <circle
          cx="210"
          cy="220"
          r={ledR}
          fill={BRAND_COLORS.ledRed}
          filter={`url(#led-${uniqueId})`}
          className={`brand-led-${uniqueId}`}
        />
      )}

      <style>{`
        @keyframes brand-pulse-run-${uniqueId} {
          0% { stroke-dashoffset: 700; opacity: 0; }
          50% { opacity: 1; }
          100% { stroke-dashoffset: -700; opacity: 0; }
        }

        @keyframes brand-led-blink-${uniqueId} {
          0%, 90% { opacity: 0; }
          92% { opacity: 1; }
          94% { opacity: 0; }
          96% { opacity: 1; }
          100% { opacity: 0; }
        }

        .brand-static-${uniqueId} {
          opacity: 1;
          transition: opacity 0.3s ease;
        }
        .group:hover .brand-static-${uniqueId} {
          opacity: 0.3;
        }

        .brand-pulse-${uniqueId} {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .group:hover .brand-pulse-${uniqueId} {
          opacity: 1;
          animation: brand-pulse-run-${uniqueId} 2s ease-in-out infinite;
        }

        .brand-led-${uniqueId} {
          animation: brand-led-blink-${uniqueId} 5s infinite;
        }
      `}</style>
    </svg>
  );
}

/**
 * Wordmark - "supa|bank" + ".xyz" stacked (CANONICAL from LogoBrand)
 */
function Wordmark({
  config,
  inverted = false,
  showDomain = true,
}: {
  config: typeof sizeConfig[LogoSize];
  inverted?: boolean;
  showDomain?: boolean;
}) {
  const { text, domain } = config;
  const isDark = inverted;
  const textColor = isDark ? '#fff' : BRAND_COLORS.obsidian;

  return (
    <div className="flex flex-col">
      <span
        className={cn('tracking-tight leading-none', text)}
        style={{ fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif", fontWeight: 800 }}
      >
        <span style={{ color: textColor }}>supa</span>
        <span style={{ color: BRAND_COLORS.accent }}>bank</span>
      </span>
      {showDomain && (
        <span
          className={cn('opacity-50 leading-tight', domain)}
          style={{
            fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif",
            fontWeight: 700,
            color: textColor
          }}
        >
          .xyz
        </span>
      )}
    </div>
  );
}

/**
 * Main Logo Component - CANONICAL
 */
export function SupabankLogo({
  variant = 'full',
  size = 'md',
  className,
  inverted = false,
  living = true, // Default to living brand
}: SupabankLogoProps) {
  const config = sizeConfig[size];
  const uniqueId = React.useId().replace(/:/g, '');

  if (variant === 'icon') {
    return (
      <div className={cn('group', className)}>
        <ZigzagGlyph
          size={size}
          config={config}
          inverted={inverted}
          showLed={living}
          hoverAnimation={living}
          uniqueId={uniqueId}
        />
      </div>
    );
  }

  if (variant === 'wordmark') {
    return (
      <Wordmark
        config={config}
        inverted={inverted}
        showDomain={true}
      />
    );
  }

  // Full variant: icon + wordmark
  return (
    <div className={cn('flex items-center group', config.gap, className)}>
      <ZigzagGlyph
        size={size}
        config={config}
        inverted={inverted}
        showLed={living}
        hoverAnimation={living}
        uniqueId={uniqueId}
      />
      <Wordmark
        config={config}
        inverted={inverted}
        showDomain={true}
      />
    </div>
  );
}

export default SupabankLogo;
